import { getUseResource } from '../resource';
import { userResources } from '../builders/userResources';
import { IAdminInfo, IBasicUserInfo, IUserInfo, IUserInfoWithOrganizations } from '../models/IUserInfo';
import { IResponse } from '../models/IResponse';
import { IPage } from '../models/IPage';
import { paginatedResourceQuery } from '../paginatedResourceQuery';
import { IServerSidePaginatedFetcherParams } from '../../contexts/Fetcher';
import { EMfaMethod } from './apiAuth';
import { IBasicOrganization } from '../models/IOrganization';
import { StringDate } from '../../types/StringDate';
import { UUID } from '../../types/uuid';

export type TUpdateUserPayload = Partial<Pick<IBasicUserInfo, 'first_name' | 'last_name' | 'phone_number'>>;
export type TCreateUserPayload = TUpdateUserPayload & Pick<IBasicUserInfo, 'email'> & { password: string };
export interface IAssignRolePayload {
  role_id: string;
  user_id: string;
  organization_id: string | null;
}

interface IMFaStatusUnset {
  mfa_enabled: false;
}
interface IMfaStatusSet {
  mfa_enabled: true;
  default_method_type: EMfaMethod;
  methods: Array<{
    method_name: EMfaMethod;
    default: boolean;
  }>;
}
export type TMfaStatus = IMFaStatusUnset | IMfaStatusSet;

export interface IMfaFullDevice {
  method_id: UUID;
  method_name: EMfaMethod;
  method_enable: boolean;
  method_created_at: StringDate;
  method_updated_at: StringDate;
  method_expiration_seconds: number;
  method_skew_seconds: number;
  method_totp_digits: number;
  method_totp_algorithm: string;
  method_allow_resend: boolean;
  method_token_pattern: string;
  device_id: UUID;
  device_name: string;
  device_can_resend: boolean;
  device_can_use_backup: boolean;
  device_is_default: boolean;
}

export const apiUser = {
  useCreateUser: getUseResource(
    userResources.createUser,
    (body): IResponse<IBasicUserInfo> => body,
    (params, payload: TCreateUserPayload) => payload
  ),
  useUpdateUser: getUseResource(
    userResources.updateUser,
    (body): IResponse<IBasicUserInfo> => body,
    (params, payload: TUpdateUserPayload) => payload
  ),
  useGetUser: getUseResource(userResources.getUser, (body): IResponse<IUserInfoWithOrganizations> => body),
  useGetUserInfo: getUseResource(userResources.getUserInfo, (body): IResponse<IUserInfo> => body),
  useGetCurrent: getUseResource(userResources.getCurrentUserInfo, (body): IResponse<IUserInfo> => body),
  useGetUsers: getUseResource(
    userResources.getUsers,
    (body): IResponse<IPage<IUserInfoWithOrganizations>> => body,
    paginatedResourceQuery
  ),
  useGetUsersByIds: getUseResource(
    userResources.getUsersByIds,
    (body): IResponse<IPage<Pick<IBasicUserInfo, 'id' | 'first_name' | 'last_name' | 'email'>>> => body,
    (params, users: string[], pagination: IServerSidePaginatedFetcherParams) => ({
      users: users.join(','),
      ...paginatedResourceQuery(params, pagination),
    })
  ),
  useGetUsersByIdsSilent: getUseResource(
    userResources.getUsersByIds,
    (body): IResponse<IPage<Pick<IBasicUserInfo, 'id' | 'first_name' | 'last_name' | 'email'>>> => body,
    (params, users: string[], pagination: IServerSidePaginatedFetcherParams) => ({
      users: users.join(','),
      ...paginatedResourceQuery(params, pagination),
    }),
    void 0,
    void 0,
    {
      indicateLoading: false,
    }
  ),
  useGetUsersByRoles: getUseResource(userResources.getUsersByRoles, (body): IResponse<IPage<IBasicUserInfo>> => body),
  useGetAdmins: getUseResource(
    userResources.getAdmins,
    (body): IResponse<IPage<IAdminInfo>> => body,
    (params, pagination: IServerSidePaginatedFetcherParams, roleIds?: string[]) => {
      return {
        role_ids: roleIds,
        ...paginatedResourceQuery(params, pagination),
      };
    }
  ),
  useAssignRole: getUseResource(
    userResources.assignRole,
    (body): IUserInfo => body,
    (params, payload: IAssignRolePayload) => payload
  ),
  useUpdateDefaultOrganization: getUseResource(
    userResources.updateDefaultOrganization,
    (body): IResponse<IBasicUserInfo> => body,
    (params, organization: Pick<IBasicOrganization, 'id'>) => ({ organization_id: organization.id }),
    (resource, apiExecutor) => {
      return async (payload): Promise<IResponse<IBasicUserInfo>> => {
        const resourceUrl = resource
          ? resource.template
              .replace(':user_id', resource.params.user_id)
              .replace(':organization_id', payload.organization_id)
          : null;

        if (!resourceUrl) {
          throw new Error('resource is missing');
        }
        const response = await apiExecutor.patch<IResponse<IBasicUserInfo>>(resourceUrl);
        return response.data;
      };
    }
  ),
  useAcceptUserAgreement: getUseResource(
    userResources.acceptUserAgreement,
    (body): IResponse<IBasicUserInfo> => body,
    (params, eula: boolean, user_license: boolean) => ({ eula, user_license })
  ),
  useRequestEmailChange: getUseResource(
    userResources.requestEmailChange,
    (body): IResponse<void> => body,
    (params, new_email: string) => ({ new_email }),
    (resource, apiExecutor) => {
      return async (payload) => {
        const url = resource ? resource.template.replace(':user_id', resource.params.user_id) : null;

        if (!url) throw new Error('resource is missing');
        const response = await apiExecutor.post<IResponse<void>>(url, null, { params: payload });
        return response.data;
      };
    }
  ),
  useSubmitEmailChange: getUseResource(
    userResources.submitEmailChange,
    (body): IResponse<void> => body,
    (params, token: string) => ({ token })
  ),
  useGetMfaStatus: getUseResource(userResources.getMfaStatus, (body): IResponse<TMfaStatus> => body),
  useGetMfaDevices: getUseResource(userResources.getMfaDevices, (body): IResponse<IMfaFullDevice[]> => body),
  useRevokeUserRole: getUseResource(
    userResources.revokeUserRole,
    (body): IResponse<void> => body,
    (params, organization_name: string | undefined) => ({ organization_name })
  ),
  useDeleteUser: getUseResource(userResources.deleteUser, (body): IResponse<void> => body),
};
